import { FALLBACK_IMAGE_URL } from "@/app/constants.mjs";
import { devDebug } from "@/util/dev.mjs";
import globals from "@/util/global-whitelist.mjs";

// HTMLImageElement not available on test reporter
const isImageElement = (
  element: EventTarget | null,
): element is HTMLImageElement => {
  return typeof HTMLImageElement !== "undefined"
    ? element instanceof HTMLImageElement
    : element instanceof HTMLElement && element.tagName === "IMG";
};

function getFallbackImage(event: Event) {
  if (!isImageElement(event.target)) return;
  if (event.target.src === FALLBACK_IMAGE_URL) return;
  devDebug(`Image failed to load`, {
    src: event.target.src,
    img: event.target,
  });
  event.target.dataset.originalSrc = event.target.src;
  event.target.src = FALLBACK_IMAGE_URL;
}

const attachedSet = new WeakSet();

const obs =
  typeof MutationObserver !== "undefined"
    ? new MutationObserver((mutations) => {
        for (const { addedNodes } of mutations) {
          for (const node of Array.from(addedNodes)) {
            if (!(node instanceof HTMLElement)) continue;
            const imgs = Array.from(
              node.querySelectorAll("img:not([data-no-fallback])"),
            );
            if (isImageElement(node)) imgs.push(node);

            for (const img of imgs) {
              if (attachedSet.has(img)) continue;
              attachedSet.add(img);
              img.addEventListener("error", getFallbackImage);
            }
          }
        }
      })
    : null;

export function observe(root = globals.document?.body) {
  obs?.observe(root, {
    childList: true,
    subtree: true,
  });
}

export function unobserve() {
  obs?.disconnect();
}
